import { Event } from '@botpress/client'
import { Composer, StylesheetProvider, WebchatTheme } from '@botpress/webchat'
import { Button, Card } from '@bpinternal/ui-kit'
import { Flex, ScrollArea, Text } from '@radix-ui/themes'
import { Conversation } from 'hitl-client'
import { DateTime } from 'luxon'
import { useEffect, useRef, useState } from 'react'
import { HiOutlineChatBubbleLeftRight } from 'react-icons/hi2'
import { useAsync, useInterval } from 'react-use'
import { getApiClientForBot } from '~/client'
import { EmptyState, Icon } from '~/elementsv2'
import { MessageItem } from '~/features/conversations/components'
import { HITLClient } from '~/features/hitl/client'
import { defaultWebchatTheme } from '~/features/webchat/stores/useConfigStore'
import { useWebchatMessages } from '~/hooks/messages'
import { useLocalStore } from '~/stores'
import { cn } from '~/utils'

type MessageListProps = {
  botId: string
  upstreamConversationId: string
  workspaceId: string
  className?: string
  assignedToCurrentUser?: boolean
  downstreamConversation: Conversation
}

export const HITLChat = ({
  botId,
  workspaceId,
  className,
  upstreamConversationId,
  assignedToCurrentUser,
  downstreamConversation,
}: MessageListProps) => {
  const getPreferredTheme = useLocalStore((s) => s.getPreferredTheme)

  const webchatTheme: WebchatTheme = {
    ...defaultWebchatTheme,
    themeMode: getPreferredTheme(),
  }

  const {
    data: fetchedMessages,
    fetchNextPage,
    hasNextPage,
    isFetching,
    refetch,
  } = useWebchatMessages({
    botId,
    conversationId: upstreamConversationId,
    workspaceId,
  })

  const [eventHitlAssigned, setEventHitlAssigned] = useState<Event>()
  const [assignedAt, setAssignedAt] = useState<Date>()

  const scrollableMessagesRef = useRef<HTMLDivElement>(null)
  const hitlClient = useHitlClient({ botId, workspaceId })

  const messages = fetchedMessages?.pages?.flatMap((page) => page.messages).reverse() ?? []

  useAsync(async () => {
    if (eventHitlAssigned?.conversationId === downstreamConversation.id) {
      return
    }

    const client = getApiClientForBot({
      botId,
      workspaceId,
    })

    const { events: hitlAssignedEvents } = await client.listEvents({
      type: 'hitl:hitlAssigned',
      conversationId: downstreamConversation.id,
    })

    setEventHitlAssigned(hitlAssignedEvents[0])
  }, [downstreamConversation.id, botId])

  useEffect(() => {
    const scrollableElement = scrollableMessagesRef.current

    if (scrollableElement) {
      scrollableElement.scrollTop = scrollableElement.scrollHeight
    }
  }, [])

  useEffect(() => {
    if (eventHitlAssigned?.createdAt) {
      setAssignedAt(new Date(eventHitlAssigned.createdAt))
    }
  }, [eventHitlAssigned])
  useInterval(refetch, 1000)

  if (!isFetching && !messages.length) {
    return (
      <Card className={cn('flex items-center justify-center border-dashed', className)}>
        <EmptyState />
        <Flex align={'center'} gap={'2'} className="opacity-60">
          <Icon color="gray" size="4" icon={HiOutlineChatBubbleLeftRight} />
          <Text color="gray">No messages in conversation</Text>
        </Flex>
      </Card>
    )
  }

  return (
    <>
      <StylesheetProvider {...webchatTheme} />
      <ScrollArea scrollbars="vertical" className="relative h-[calc(100vh-15rem)]" ref={scrollableMessagesRef}>
        <Flex
          direction={'column'}
          gap={'2'}
          px={'8'}
          flexGrow={'1'}
          ref={scrollableMessagesRef}
          className={cn('mb-4 border-x-[1px] border-solid border-gray-6', className)}
        >
          <Button
            color="gray"
            variant="ghost"
            className="mx-auto my-2 w-fit"
            onClick={() => void fetchNextPage()}
            disabled={!hasNextPage}
          >
            {hasNextPage ? 'Load more' : 'End of conversation'}
          </Button>
          {messages?.map((message, i, arr) => (
            <div key={message.id} className={cn({ ['mb-24']: i === arr.length - 1 })}>
              <MessageItem disableLink {...message} />
              {assignedAt &&
              assignedAt.getTime() > (message?.timestamp?.getTime() ?? 0) &&
              (!messages[i + 1] || assignedAt.getTime() < (messages[i + 1]?.timestamp?.getTime() ?? 0)) ? (
                <EscalatedMarker date={assignedAt} />
              ) : null}
            </div>
          ))}
        </Flex>
        <div className="absolute inset-x-0 bottom-0  border-x border-gray-6 bg-background px-8 pb-2 pt-1">
          <div className="absolute inset-x-0 -top-8 h-8 bg-gradient-to-t from-background to-transparent" />
          <Composer
            className="rounded-xl shadow-lg"
            sendMessage={(payload) => hitlClient?.sendMessage(payload, downstreamConversation.id)}
            composerPlaceholder={assignedToCurrentUser ? 'Enter message...' : 'You must be assigned to respond'}
            showPoweredBy={false}
            connected={assignedToCurrentUser && !!hitlClient}
          />
        </div>
      </ScrollArea>
    </>
  )
}

const EscalatedMarker = ({ date }: { date: Date }) => (
  <div className="my-3 mb-1 flex w-full items-center justify-center">
    <div className="ml-8 h-[1px] flex-grow bg-gray-8" />
    <div className="mx-4 text-sm text-gray-8">
      Conversation Escalated &#8226; {DateTime.fromJSDate(date).toRelative()}
    </div>
    <div className="mr-8 h-[1px] flex-grow bg-gray-8" />
  </div>
)

type UseHitlClientParams = {
  botId: string
  workspaceId: string
}

const useHitlClient = ({ botId, workspaceId }: UseHitlClientParams) => {
  const [client, setClient] = useState<HITLClient>()

  useAsync(async () => {
    if (!client || client.workspaceId !== workspaceId || client.botId !== botId) {
      try {
        const newClient = await HITLClient.create({
          botId,
          workspaceId,
        })
        setClient(newClient)
      } catch (err) {
        console.error('Failed to create HITL client:', err)
      }
    }
  }, [botId, workspaceId])

  return client
}
